import React, { useEffect, useState } from 'react';
import { api } from 'lib/api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { usePopup } from 'context/PopupProvider';
import {
  Sliders,
  Coins,
  Plus,
  Trash2,
  Pencil,
  X,
  RefreshCw,
  Save,
  Check,
  Timer,
  Lock,
} from 'lucide-react';
import type { Currency, GameConfig } from 'types/admin';
import { useAuth } from 'context/AuthProvider';
import { listRowNumber, LIST_TD_NO_CLASS, LIST_TH_NO_CLASS, LIST_TABLE_CLASS, LIST_THEAD_CLASS, LIST_TBODY_CLASS, LIST_TH_CLASS, LIST_TD_CLASS } from 'lib/list-table';

interface GameOption {
  id: number;
  gameId: string;
  name: string;
  status: boolean;
}

const emptyCurrencyForm = { code: '', name: '', symbol: '' };

const TEEN_PATTI_GAME_SLUGS = new Set(['teen-patti', 'teenpatti']);

function resolveDefaultGameId(games: GameOption[]): number | null {
  if (!games.length) return null;
  const teenPatti = games.find((game) => {
    const slug = game.gameId.trim().toLowerCase().replace(/_/g, '-');
    return TEEN_PATTI_GAME_SLUGS.has(slug);
  });
  return (teenPatti ?? games[0]).id;
}

/** Local form state — numeric fields may be '' while the user is editing. */
type ConfigFormState = Omit<
  GameConfig,
  'turn_expiry_timer' | 'hand_start_timer' | 'house_rake_percentage' | 'absolute_max_pot_ceiling' | 'side_show_auto_reject_limit'
> & {
  turn_expiry_timer: number | '';
  hand_start_timer: number | '';
  house_rake_percentage: number | '';
  absolute_max_pot_ceiling: number | '';
  side_show_auto_reject_limit: number | '';
};

function ConfigField({
  label,
  hint,
  children,
}: {
  label?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      {label ? (
        <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-muted">{label}</label>
      ) : null}
      {children}
      {hint ? <p className="mt-1.5 text-[11px] leading-relaxed text-muted">{hint}</p> : null}
    </div>
  );
}

function ConfigInput({
  suffix,
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { suffix?: string }) {
  return (
    <div className="relative">
      <input
        {...props}
        className={`block w-full rounded-xl border border-line bg-card px-3.5 py-2.5 text-sm text-heading shadow-sm transition focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/20 disabled:bg-subtle disabled:text-muted ${suffix ? 'pr-12' : ''} ${className}`}
      />
      {suffix ? (
        <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-xs font-semibold text-muted">
          {suffix}
        </span>
      ) : null}
    </div>
  );
}

function ConfigSelect({
  className = '',
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`block w-full rounded-xl border border-line bg-card px-3.5 py-2.5 text-sm text-heading shadow-sm transition focus:border-brand-gold focus:outline-none focus:ring-2 focus:ring-brand-gold/20 ${className}`}
    />
  );
}

function ConfigSection({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string;
  description?: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-line bg-subtle/40 p-4 sm:p-5">
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-card text-heading shadow-sm ring-1 ring-line">
          <Icon size={17} />
        </div>
        <div>
          <h4 className="text-sm font-bold text-heading">{title}</h4>
          {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

const GameConfiguration = () => {
  const { confirm } = usePopup();
  const { admin } = useAuth();
  const canManageCurrencies = Boolean(admin?.isSuperAdmin);

  const [games, setGames] = useState<GameOption[]>([]);
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null);
  const [config, setConfig] = useState<ConfigFormState>({
    turn_expiry_timer: 15,
    hand_start_timer: 10,
    house_rake_percentage: 5,
    absolute_max_pot_ceiling: 10000,
    side_show_auto_reject_limit: 3,
    private_table_min_amount: null,
    private_table_max_amount: null,
  });
  const [loading, setLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [notice, setNotice] = useState<{ text: string; ok: boolean } | null>(null);

  const [curModalOpen, setCurModalOpen] = useState(false);
  const [editingCurId, setEditingCurId] = useState<number | null>(null);
  const [curForm, setCurForm] = useState(emptyCurrencyForm);
  const [curModalMsg, setCurModalMsg] = useState('');

  const notify = (text: string, ok: boolean) => {
    setNotice({ text, ok });
    setTimeout(() => setNotice(null), 3500);
  };

  const fetchCurrencies = () => {
    api.get<Currency[]>(API_ENDPOINTS.ADMIN.CURRENCIES.GET_ALL)
      .then((res) => {
        if (res.success && res.data) setCurrencies(res.data);
      })
      .catch((err) => console.error('Failed to fetch currencies:', err));
  };

  const fetchGameConfig = (gameDbId: number) => {
    setConfigLoading(true);
    api.get<GameConfig>(API_ENDPOINTS.ADMIN.GAMES.GET_CONFIG(gameDbId))
      .then((res) => {
        if (res.success && res.data) {
          const houseRakeValue = Number(res.data.house_rake_percentage);
          setConfig({
            gameId: res.data.gameId,
            gameName: res.data.gameName,
            turn_expiry_timer: Number(res.data.turn_expiry_timer),
            hand_start_timer: Number(res.data.hand_start_timer) || 10,
            house_rake_percentage: Number.isFinite(houseRakeValue) ? houseRakeValue : 0,
            absolute_max_pot_ceiling: Number(res.data.absolute_max_pot_ceiling),
            side_show_auto_reject_limit: Number(res.data.side_show_auto_reject_limit ?? 3),
            private_table_min_amount:
              res.data.private_table_min_amount != null ? Number(res.data.private_table_min_amount) : null,
            private_table_max_amount:
              res.data.private_table_max_amount != null ? Number(res.data.private_table_max_amount) : null,
          });
        }
      })
      .catch((err) => console.error('Failed to fetch game config:', err))
      .finally(() => setConfigLoading(false));
  };

  useEffect(() => {
    Promise.all([
      api.get<GameOption[]>(API_ENDPOINTS.ADMIN.GAMES.GET_ALL),
      api.get<Currency[]>(API_ENDPOINTS.ADMIN.CURRENCIES.GET_ALL),
    ])
      .then(([gamesRes, currenciesRes]) => {
        if (gamesRes.success && Array.isArray(gamesRes.data)) {
          setGames(gamesRes.data);
          setSelectedGameId(resolveDefaultGameId(gamesRes.data));
        }
        if (currenciesRes.success && currenciesRes.data) setCurrencies(currenciesRes.data);
      })
      .catch((err) => console.error('Failed to fetch configuration:', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedGameId != null) fetchGameConfig(selectedGameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when selected game changes only
  }, [selectedGameId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setConfig({
      ...config,
      [name]: value === '' ? '' : Number(value),
    });
  };

  // Private-table amount bounds are optional — an empty input clears them to null.
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setConfig({ ...config, [name]: value === '' ? null : Number(value) });
  };

  const handleSaveConfig = () => {
    if (selectedGameId == null) {
      setMessage('Select a registered game first.');
      return;
    }
    const turnExpiry = Number(config.turn_expiry_timer);
    const handStart = Number(config.hand_start_timer);
    const houseRake = Number(config.house_rake_percentage);
    const potCeiling = Number(config.absolute_max_pot_ceiling);
    const sideShowRejectLimit = Number(config.side_show_auto_reject_limit);

    if (!Number.isFinite(turnExpiry) || turnExpiry < 5) {
      setMessage('Enter a valid turn expiry timer (minimum 5 seconds).');
      return;
    }
    if (!Number.isFinite(handStart) || handStart < 5 || handStart > 120) {
      setMessage('Enter a valid hand start timer between 5 and 120 seconds.');
      return;
    }
    if (!Number.isFinite(houseRake) || houseRake < 0 || houseRake > 100) {
      setMessage('Enter a valid house rake between 0 and 100%.');
      return;
    }
    if (!Number.isFinite(potCeiling) || potCeiling < 0) {
      setMessage('Enter a valid absolute max pot ceiling.');
      return;
    }
    if (!Number.isFinite(sideShowRejectLimit) || sideShowRejectLimit < 0 || sideShowRejectLimit > 20) {
      setMessage('Enter a valid side-show auto-reject limit between 0 and 20.');
      return;
    }
    // Guard: when both bounds are set, min must not exceed max.
    if (
      config.private_table_min_amount != null &&
      config.private_table_max_amount != null &&
      config.private_table_min_amount > config.private_table_max_amount
    ) {
      setMessage('Private table minimum cannot be greater than the maximum.');
      return;
    }
    setSaving(true);
    setMessage('');
    const payload: GameConfig = {
      ...config,
      turn_expiry_timer: turnExpiry,
      hand_start_timer: handStart,
      house_rake_percentage: houseRake,
      absolute_max_pot_ceiling: potCeiling,
      side_show_auto_reject_limit: sideShowRejectLimit,
    };
    api.post(API_ENDPOINTS.ADMIN.GAMES.UPDATE_CONFIG(selectedGameId), payload)
      .then((res) => {
        setMessage(res.success ? 'Configuration saved successfully!' : 'Failed to save configuration.');
        setSaving(false);
      })
      .catch((err) => {
        console.error('Failed to save config:', err);
        setMessage('Error saving configuration.');
        setSaving(false);
      });
  };

  // ---- Currency actions ----
  const openCreateCurrency = () => {
    setEditingCurId(null);
    setCurForm(emptyCurrencyForm);
    setCurModalMsg('');
    setCurModalOpen(true);
  };

  const openEditCurrency = (c: Currency) => {
    setEditingCurId(c.id);
    setCurForm({ code: c.code, name: c.name, symbol: c.symbol });
    setCurModalMsg('');
    setCurModalOpen(true);
  };

  const submitCurrency = (e: React.FormEvent) => {
    e.preventDefault();
    setCurModalMsg('');
    if (editingCurId === null) {
      api.post(API_ENDPOINTS.ADMIN.CURRENCIES.CREATE, {
        code: curForm.code,
        name: curForm.name,
        symbol: curForm.symbol,
      })
        .then((res) => {
          if (res.success) {
            setCurModalOpen(false);
            notify('Currency added.', true);
            fetchCurrencies();
          } else {
            setCurModalMsg(res.message === 'Resource already exists' ? 'A currency with that code already exists.' : (res.message || 'Failed to add currency.'));
          }
        })
        .catch(() => setCurModalMsg('Error adding currency.'));
    } else {
      api.put(API_ENDPOINTS.ADMIN.CURRENCIES.UPDATE(editingCurId), {
        name: curForm.name,
        symbol: curForm.symbol,
      })
        .then((res) => {
          if (res.success) {
            setCurModalOpen(false);
            notify('Currency updated.', true);
            fetchCurrencies();
          } else {
            setCurModalMsg(res.message || 'Failed to update currency.');
          }
        })
        .catch(() => setCurModalMsg('Error updating currency.'));
    }
  };

  const deleteCurrency = async (c: Currency) => {
    const ok = await confirm({
      title: 'Delete Currency',
      message: `Remove "${c.name}" (${c.code}) from gameplay currencies?`,
      variant: 'danger',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
    });
    if (!ok) return;
    api.delete(API_ENDPOINTS.ADMIN.CURRENCIES.DELETE(c.id))
      .then((res) => {
        if (res.success) {
          notify('Currency deleted.', true);
          fetchCurrencies();
        } else {
          notify(
            res.message === 'Operation not permitted'
              ? 'Cannot delete a currency assigned to a consumer or held by players.'
              : (res.message || 'Failed to delete currency.'),
            false
          );
        }
      })
      .catch(() => notify('Error deleting currency.', false));
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted font-medium">
        <RefreshCw className="animate-spin mr-2" size={20} /> Loading configuration...
      </div>
    );
  }

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-heading">Platform Configuration</h1>
          <p className="mt-1 text-sm text-muted">
            Global currency registry and per-game runtime parameters (timers, rake, private tables).
          </p>
        </div>
      </div>

      {notice && (
        <div className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-sm font-semibold ${
          notice.ok ? 'border-green-200 bg-green-500/10 text-green-700' : 'border-red-200 bg-red-500/10 text-brand-danger'
        }`}>
          {notice.ok ? <Check size={16} /> : <X size={16} />} {notice.text}
        </div>
      )}

      {/* Per-game parameters */}
      <div className="overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
        <div className="flex flex-col gap-4 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-blue-500/10 p-2.5 text-heading">
              <Sliders size={20} />
            </div>
            <div>
              <h2 className="font-bold text-heading">Core Game Parameters</h2>
              <p className="text-xs text-muted">Timers, rake, pot limits, and private-table bounds for one registered game.</p>
            </div>
          </div>
          {games.length > 0 ? (
            <div className="min-w-[220px]">
              <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-muted">Registered game</label>
              <ConfigSelect
                value={selectedGameId ?? ''}
                onChange={(e) => setSelectedGameId(Number(e.target.value))}
              >
                {games.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name} ({g.gameId}){g.status ? '' : ' — inactive'}
                  </option>
                ))}
              </ConfigSelect>
            </div>
          ) : null}
        </div>

        {games.length === 0 ? (
          <div className="p-6 text-sm text-muted">
            No games registered yet. Add a game under Game Management before configuring parameters.
          </div>
        ) : (
        <div className={`space-y-5 p-5 sm:p-6 transition-opacity ${configLoading ? 'pointer-events-none opacity-50' : ''}`}>
          <ConfigSection
            title="Timers & pot limits"
            description="Controls turn timeout, house rake, and maximum pot size."
            icon={Timer}
          >
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <ConfigField label="Turn expiry timer">
                <ConfigInput
                  type="number"
                  name="turn_expiry_timer"
                  min={5}
                  value={config.turn_expiry_timer}
                  onChange={handleChange}
                  suffix="sec"
                />
              </ConfigField>
              <ConfigField
                label="Hand start timer"
                hint="Countdown before a new hand, next hand, and shar vote."
              >
                <ConfigInput
                  type="number"
                  name="hand_start_timer"
                  min={5}
                  max={120}
                  value={config.hand_start_timer}
                  onChange={handleChange}
                  suffix="sec"
                />
              </ConfigField>
              <ConfigField label="House rake">
                <ConfigInput
                  type="number"
                  name="house_rake_percentage"
                  step="0.01"
                  min={0}
                  max={100}
                  value={config.house_rake_percentage}
                  onChange={handleChange}
                  suffix="%"
                />
              </ConfigField>
              <ConfigField label="Absolute max pot ceiling">
                <ConfigInput
                  type="number"
                  name="absolute_max_pot_ceiling"
                  min={0}
                  value={config.absolute_max_pot_ceiling}
                  onChange={handleChange}
                />
              </ConfigField>
              <ConfigField
                label="Side-show auto-reject limit"
                hint="After a player rejects this many side shows in one hand, further requests to them are auto-refused."
              >
                <ConfigInput
                  type="number"
                  name="side_show_auto_reject_limit"
                  min={0}
                  max={20}
                  value={config.side_show_auto_reject_limit}
                  onChange={handleChange}
                />
              </ConfigField>
            </div>
          </ConfigSection>

          <ConfigSection
            title="Private table stakes"
            description="Optional bounds when players create private tables."
            icon={Lock}
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
              <ConfigField
                label="Minimum amount"
                hint="Lowest stake allowed when a player creates a private table."
              >
                <ConfigInput
                  type="number"
                  name="private_table_min_amount"
                  min={0}
                  step="0.01"
                  value={config.private_table_min_amount ?? ''}
                  onChange={handleAmountChange}
                  placeholder="No minimum"
                />
              </ConfigField>
              <ConfigField
                label="Maximum amount"
                hint="Highest stake allowed for a private table. Leave blank for no cap."
              >
                <ConfigInput
                  type="number"
                  name="private_table_max_amount"
                  min={0}
                  step="0.01"
                  value={config.private_table_max_amount ?? ''}
                  onChange={handleAmountChange}
                  placeholder="No maximum"
                />
              </ConfigField>
            </div>
          </ConfigSection>

          <div className="flex flex-col-reverse gap-3 border-t border-line pt-5 sm:flex-row sm:items-center sm:justify-between">
            {message ? (
              <p
                className={`text-sm font-semibold ${
                  message.includes('Error') || message.includes('Failed') || message.includes('cannot')
                    ? 'text-brand-danger'
                    : 'text-green-600'
                }`}
              >
                {message}
              </p>
            ) : (
              <p className="hidden text-xs text-muted sm:block">Changes apply immediately after save.</p>
            )}
            <button
              type="button"
              onClick={handleSaveConfig}
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-dark px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-opacity-90 disabled:opacity-50 sm:ml-auto sm:w-auto"
            >
              <Save size={16} /> {saving ? 'Saving…' : 'Save parameters'}
            </button>
          </div>
        </div>
        )}
      </div>

      {/* Platform gameplay currencies */}
      <div className="overflow-hidden rounded-2xl border border-line bg-card shadow-sm">
        <div className="flex flex-col gap-4 border-b border-line px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-amber-500/10 p-2.5 text-brand-gold">
              <Coins size={20} />
            </div>
            <div>
              <h2 className="font-bold text-heading">Gameplay Currencies</h2>
              <p className="text-xs text-muted">
                Registry of currency codes (USD, INR, coins). Super Admin can add codes; each consumer uses its own wallet currency.
              </p>
            </div>
          </div>
          {canManageCurrencies ? (
            <button
              type="button"
              onClick={openCreateCurrency}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-gold px-4 py-2.5 text-sm font-bold text-brand-dark shadow-sm transition hover:bg-opacity-95"
            >
              <Plus size={18} /> Add currency
            </button>
          ) : null}
        </div>

        <div className="overflow-x-auto">
          <table className={LIST_TABLE_CLASS}>
            <thead className={LIST_THEAD_CLASS}>
              <tr>
                <th className={`${LIST_TH_NO_CLASS} px-6 py-4`}>No.</th>
                <th className={LIST_TH_CLASS}>Symbol</th>
                <th className={LIST_TH_CLASS}>Code</th>
                <th className={LIST_TH_CLASS}>Name</th>
                <th className={LIST_TH_CLASS}>Status</th>
                {canManageCurrencies ? (
                  <th className={LIST_TH_CLASS}>Actions</th>
                ) : null}
              </tr>
            </thead>
            <tbody className={LIST_TBODY_CLASS}>
              {currencies.length > 0 ? (
                currencies.map((c, index) => (
                  <tr key={c.id} className="hover:bg-subtle/50 transition-colors">
                    <td className={`${LIST_TD_NO_CLASS} px-6 py-4`}>{listRowNumber(1, index, 0)}</td>
                    <td className={`${LIST_TD_CLASS} text-2xl leading-none`}>{c.symbol}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm font-bold text-heading`}>{c.code}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm text-body`}>{c.name}</td>
                    <td className={`${LIST_TD_CLASS} whitespace-nowrap`}>
                      <span
                        className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full border ${
                          c.status
                            ? 'bg-green-500/10 text-green-700 border-green-200'
                            : 'bg-subtle text-muted border-line'
                        }`}
                      >
                        {c.status ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    {canManageCurrencies ? (
                      <td className={`${LIST_TD_CLASS} whitespace-nowrap text-sm`}>
                        <div className="flex gap-3">
                          <button onClick={() => openEditCurrency(c)} className="p-1 text-blue-600 hover:bg-blue-500/10 rounded" title="Edit currency">
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => deleteCurrency(c)}
                            className="p-1 rounded text-brand-danger hover:bg-red-500/10"
                            title="Delete currency"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canManageCurrencies ? 6 : 5} className="px-6 py-10 text-center text-muted font-medium">
                    No gameplay currencies configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / edit currency modal */}
      {curModalOpen && (
        <div className="fixed inset-0 bg-brand-dark/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm animate-fadeIn">
          <div className="bg-card w-full max-w-md rounded-2xl shadow-xl overflow-hidden animate-scaleIn border border-line">
            <div className="p-6 border-b border-line flex items-center justify-between bg-gradient-to-r from-brand-dark to-slate-900 text-white">
              <div>
                <h4 className="font-bold text-lg text-brand-gold">{editingCurId === null ? 'Add Currency' : 'Edit Currency'}</h4>
                <p className="text-xs text-faint">Define a currency players can play with</p>
              </div>
              <button onClick={() => setCurModalOpen(false)} className="text-muted hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={submitCurrency} className="p-6 space-y-4">
              {curModalMsg && (
                <div className="px-4 py-2.5 rounded-xl text-xs font-semibold border bg-red-500/10 text-brand-danger border-red-200">
                  {curModalMsg}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Code</label>
                  <input
                    type="text"
                    required
                    disabled={editingCurId !== null}
                    placeholder="e.g. USD"
                    value={curForm.code}
                    onChange={(e) => setCurForm({ ...curForm, code: e.target.value.toUpperCase() })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border uppercase disabled:bg-subtle disabled:text-muted"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Symbol</label>
                  <input
                    type="text"
                    required
                    placeholder="$"
                    value={curForm.symbol}
                    onChange={(e) => setCurForm({ ...curForm, symbol: e.target.value })}
                    className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border text-center"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-muted uppercase tracking-wider mb-2">Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. US Dollar"
                  value={curForm.name}
                  onChange={(e) => setCurForm({ ...curForm, name: e.target.value })}
                  className="block w-full rounded-lg border-line shadow-sm focus:border-brand-gold focus:ring-brand-gold sm:text-sm p-3 border"
                />
                {editingCurId !== null && <p className="text-[11px] text-muted mt-1">Code cannot be changed after creation.</p>}
              </div>

              <div className="flex gap-4 pt-2">
                <button type="button" onClick={() => setCurModalOpen(false)}
                  className="flex-1 px-4 py-2.5 bg-subtle hover:bg-subtle text-body rounded-lg font-semibold border border-line transition-colors text-sm">
                  Cancel
                </button>
                <button type="submit"
                  className="flex-1 px-4 py-2.5 bg-brand-gold hover:bg-opacity-95 text-brand-dark rounded-lg font-bold transition-all text-sm shadow-sm">
                  {editingCurId === null ? 'Add Currency' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GameConfiguration;
